#!/usr/bin/env node
/**
 * Carrier MCP — single-user stdio entry point (MCPB / DXT bundle target).
 *
 * Reads CARRIER_OCS_API_TOKEN from env; calls OCS directly with that token.
 * No OAuth, no Workers — for local Claude Desktop installs via the .mcpb bundle.
 *
 * For multi-user deployments, use the remote OAuth endpoint at mcp.carrier.llc/mcp.
 *
 * Tool registry MUST mirror agent.ts exactly so that generate-docs / verify-fidelity
 * produce the same tool list as the deployed Worker.
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";
import { registerAllTools, type ToolContext } from "./tools.js";
import { registerIntelligenceTools } from "./intelligence.js";
import { registerAllBacklogTools } from "./tools-backlog.js";
import { registerAllCarrierAskTools } from "./tools-carrier-ask.js";
import { registerListRecentOcsEventsTool } from "./list-recent-ocs-events.js";
import { registerRateLimitStatusTool } from "./tools-rate-governor.js";
import { registerDepletionEventsTool } from "./tools-depletion-events.js";
import { registerCountryHistoryTool } from "./tools-country-history.js";
import { registerAllApps } from "./apps/index.js";
import { registerAllPrompts } from "./prompts.js";
import { registerAllPricingTools } from "./tools-pricing.js";
import { registerScheduleAndUsageTools } from "./tools-ui-agent-schedule.js";
import { registerAllUiAgentTools } from "./tools-ui-agent.js";
import { registerUiAgentAskTools } from "./tools-ui-agent-ask.js";
import { registerStripeConnectTools } from "./stripe-connect-tools.js";
import { registerWalletTools } from "./wallet-tools.js";
import { registerGreenzoneTools } from "./tools-greenzone.js";
import { registerUiAgentGenericTools } from "./tools-ui-agent-generic.js";
import { createUiAgentRuntime } from "./ui-agent-runtime.js";
import { readPortalOptIn, warnPortalOptIn } from "./portal-credentials.js";
import { registerStorefrontLogoTools } from "./tools-storefront-logo.js";
import { registerStorefrontDeployTools } from "./tools-storefront-deploy.js";
import { installAnnotationDefaults } from "./tool-annotations.js";
import { ALL_TOOL_SCOPES } from "./tool-registry.js";
import type { CarrierProps, AuditRow, Env } from "./types.js";
import { CARRIER_VERSION } from "./version.js";

const baseUrl =
  process.env.CARRIER_OCS_BASE_URL ??
  process.env.ESIMVAULT_BASE_URL ??
  "https://ocs.esimvault.cloud";
const token =
  process.env.CARRIER_OCS_API_TOKEN ?? process.env.ESIMVAULT_API_TOKEN;

if (!token) {
  console.error(
    "ERROR: CARRIER_OCS_API_TOKEN (or legacy ESIMVAULT_API_TOKEN) must be set.",
  );
  process.exit(1);
}

// Minimal env shim — stdio mode never touches KV/R2/AE/secrets at runtime.
// CF-only bindings (OAUTH_KV, OCS_EVENT_ROUTING, ASSETS, CARRIER_USERS, DOWNLOADS)
// are stubbed so that tool *registration* succeeds; their handlers would error if
// actually invoked in stdio mode (acceptable — these tools require the Workers runtime).
const stdioEnv = {
  CARRIER_OCS_BASE_URL: baseUrl,
  // Empty on purpose. rotate_credentials reads this and throws a clear
  // message rather than pretending a local install can rotate the vault.
  CARRIER_TOKEN_ENCRYPTION_KEY: "",
  AUDIT_LOG: { writeDataPoint: (_: unknown) => undefined },
  OAUTH_KV: null,
  OCS_EVENT_ROUTING: null,
  ASSETS: null,
  CARRIER_USERS: null,
  DOWNLOADS: null,
  STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY ?? "",
  // Browser Use Cloud. Read from the environment with a real fallback, the way
  // STRIPE_SECRET_KEY above and the wallet values below are — unlike the
  // MANUS_API_KEY this replaces, which was hardcoded to "" with no fallback at
  // all, so every ui_agent_* tool returned a configuration error to every
  // caller from the day it shipped. Absent is still a valid state: the tools
  // stay registered and say so. Runs are billed to this key at ~$0.20-1.00
  // each, so dispatch is capped per process — see ui-agent-runtime.ts.
  BROWSER_USE_API_KEY: process.env.BROWSER_USE_API_KEY,
  // Prepaid-wallet configuration. Read from the environment so a local operator
  // who holds these values gets working wallet tools; when absent the tools stay
  // registered (catalog parity with the Worker) and return an explicit
  // configuration error instead of silently disappearing from tools/list.
  ATLAS_BASE_URL: process.env.ATLAS_BASE_URL,
  CARRIER_INTERNAL_API_KEY: process.env.CARRIER_INTERNAL_API_KEY,
  WALLET_CHECKOUT_SUCCESS_URL: process.env.WALLET_CHECKOUT_SUCCESS_URL,
  WALLET_CHECKOUT_CANCEL_URL: process.env.WALLET_CHECKOUT_CANCEL_URL,
  // Bedrock credentials for carrier_ask, the natural-language router. None of
  // these appeared in this shim at all until now, so every read saw `undefined`
  // and the routing gate in tools-carrier-ask.ts could never open: the flagship
  // router had routed nothing from npm since it shipped. Same treatment as
  // STRIPE_SECRET_KEY and the wallet values above — read the environment, and
  // when absent let the tool refuse out loud rather than pretend it answered.
  AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
  AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
  AWS_SESSION_TOKEN: process.env.AWS_SESSION_TOKEN,
  AWS_REGION: process.env.AWS_REGION,
  BEDROCK_MODEL_ID: process.env.BEDROCK_MODEL_ID,
  // Defaults ON here, unlike the Worker. The flag is the Worker's staged
  // rollout switch; a local operator who has put AWS credentials on the process
  // has already opted in, and making them set a second variable they have never
  // heard of is just the same dead gate one step further along. Setting it to
  // anything other than "true" still turns the router off.
  CARRIER_ASK_ENABLED: process.env.CARRIER_ASK_ENABLED ?? "true",
  BILLING_PRIMARY: process.env.BILLING_PRIMARY,
} as unknown as Env;

const props: CarrierProps = {
  sub: "stdio@local",
  reseller_id: 0,
  reseller_name: "stdio",
  tier: "enterprise",
  scope: ["read", "write", "admin"],
};

const audit = (_row: AuditRow): void => {
  // no-op in stdio mode
};

const getUserToken = async (_sub: string): Promise<string> => token;

// managedAuth is empty here on purpose: a stdio operator brings their own OCS
// token, so that token is the tenant boundary and the managed-mode preflights
// are no-ops. It is still carried on the context so the tools that need it can
// ask, instead of silently having no way to check (which is how the Worker's
// list_recent_ocs_events shipped its cross-tenant read).
const toolCtx: ToolContext = {
  env: stdioEnv,
  props,
  managedAuth: {},
  audit,
  getUserToken,
};

const server = new McpServer(
  { name: "carrier-mcp", version: CARRIER_VERSION },
  {
    instructions:
      "Carrier MCP — single-user stdio mode. Full tool registry (mirrors the deployed Worker).",
  },
);

// Backfill the MCP safety hints (readOnly/destructive/idempotent/openWorld)
// from each tool's registered scope. Must run before the first registerTool:
// it wraps that method as registrations go through it.
//
// Without this the published CLI shipped 123 tools with 122 missing hints,
// while the deployed Worker had all of them (#723) — apps/mcp-stdio is a
// separate copy of the tool layer and nothing done in apps/mcp-server reaches
// it. Explicit annotations at a call site still win; this only fills gaps.
installAnnotationDefaults(server, ALL_TOOL_SCOPES);

// v1: 43 OCS API wrappers
registerAllTools(server, toolCtx);
// v1 intelligence: 8 AI composite tools
registerIntelligenceTools(server, toolCtx);
// v1.1 backlog tools, registered from tools-backlog.ts
registerAllBacklogTools(server, toolCtx);
// NL router: carrier_ask + carrier_ask_describe
registerAllCarrierAskTools(server, toolCtx);
// OCS event ring-buffer tool
// toolCtx, not stdioEnv: the tool reads a per-ICCID buffer and needs
// managedAuth to refuse another tenant's ICCID.
registerListRecentOcsEventsTool(server, toolCtx);
// CAR-39 wave-2: rate-limit governor status tool
registerRateLimitStatusTool(server, toolCtx);
// CAR-38 wave-2: country.entered history (KV; mirrors agent.ts)
registerCountryHistoryTool(server, toolCtx);
// CAR-42 wave-2: bundle depletion events from prepaid-usage webhook
registerDepletionEventsTool(server, toolCtx);
// v1.2 MCP Apps (fleet-health, provisioning-wizard, balance-topup)
registerAllApps(server, toolCtx);
// Pricing + projects tools
registerAllPricingTools(server, toolCtx);
// Browser-agent runtime for this process: BROWSER_USE_API_KEY from the
// environment, a Map-backed store standing in for the Worker's KV, and the
// per-process run budget that replaces the Worker's KV-backed reserveAiCalls.
const uiAgentRuntime = createUiAgentRuntime();
// The OCS-dashboard opt-in, read once here because this is where the process
// reads its environment. The warning goes to stderr — stdout is the MCP
// JSON-RPC channel — and fires at startup rather than only at call time,
// because these ten tools are usually invoked by an agent and the human who set
// the variable may never call one by hand. See portal-credentials.ts.
const portalOptIn = readPortalOptIn();
warnPortalOptIn(portalOptIn);
// UI agent schedule stubs + live session-scoped usage
registerScheduleAndUsageTools(server, toolCtx, uiAgentRuntime);
// UI agent write tools (OCS dashboard). Live only when the operator has both
// set CARRIER_ALLOW_PORTAL_PASSWORD and supplied the portal login; otherwise
// these refuse exactly as they did before the opt-in existed.
registerAllUiAgentTools(server, toolCtx, uiAgentRuntime, portalOptIn);
// UI agent ask/reply tools
registerUiAgentAskTools(server, toolCtx, uiAgentRuntime);
// Stripe Connect + Radar tools
registerStripeConnectTools(server, { env: stdioEnv, props });
// Prepaid wallet: balance read, bulk-pack Checkout, off-session auto-top-up.
// Registered on the same terms as apps/mcp-server/src/stdio.ts so the published
// npm catalog matches the hosted Worker (see issue #665). Ported verbatim — the
// wallet_auto_topup confirmation/idempotency semantics are NOT relaxed here.
registerWalletTools(server, { env: stdioEnv, props });
// CAR-41 — Greenzone whitelist portal automation
registerGreenzoneTools(server, toolCtx);
// v2.2 — ui_agent_ask: generic Steel browsing agent dispatch + ui_agent_status polling
registerUiAgentGenericTools(server, toolCtx, uiAgentRuntime);
registerStorefrontLogoTools(
  server,
  stdioEnv as unknown as Record<string, string | undefined>,
  props,
);
// Local-only: these drive host CLIs (wrangler/vercel/netlify/flyctl) on this box.
registerStorefrontDeployTools(server, props);
registerAllPrompts(server);

const transport = new StdioServerTransport();
await server.connect(transport);
